import List from "components/list";
import { useParams } from "react-router-dom";
import { getEmployeeFields } from "components/user/config";
import { UserCell } from "components/user/cell";
import { formatDateForDisplay } from "@/lib/utils";
import { useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { updateItemInCollection } from "directus/src/operations";
import { eventBus } from "utils/eventBus";
import { toast } from "sonner";
import { Button } from "ui/index";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "ui/components/dialog";
import ButtonWithLoading from "@/components/button-with-loading";
import { invalidateAllQueries } from "utils/utils";
import { ROUTE } from "@/lib/utils";

export default function TimesheetReviewerList() {
  const { employeeId } = useParams();
  const [selectedItems, setSelectedItems] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const reviewerFilter = employeeId ? { _eq: employeeId } : { user: { _eq: "$CURRENT_USER" } };

  const storageKey = employeeId ? `profile-reviewer-timesheet-${employeeId}` : "profile-reviewer-timesheet";

  const fields = [
    "id",
    "date",
    "logged_hours",
    "unapproved_hours",
    "job.name",
    "project.name",
    getEmployeeFields("employee"),
  ];

  const columns = [
    {
      id: "date",
      header: "Date",
      accessorKey: "date",
      cell: (info) => formatDateForDisplay(info.getValue()),
    },
    {
      id: "project",
      header: "Project",
      accessorKey: "project.name",
    },
    {
      id: "job",
      header: "Job",
      accessorKey: "job.name",
    },
    {
      id: "employee",
      header: "Employee",
      accessorKey: "employee",
      cell: UserCell,
    },
    {
      id: "logged_hours",
      header: "Logged",
      accessorKey: "logged_hours",
      cell: (info) => {
        const hours = info.getValue();
        return hours ? hours : "0";
      },
    },
  ];

  const filters = {
    reviewer: reviewerFilter,
    logged_hours: { _gt: 0 },
    approved_hours: { _null: true },
  };

  const approveMutation = useMutation({
    mutationFn: async (items) => {
      await Promise.all(
        items.map((item) =>
          updateItemInCollection("timesheet", item.id, {
            approved_hours: item.logged_hours,
            unapproved_hours: 0,
          })
        )
      );
    },
    onSuccess: () => {
      toast.success(
        selectedItems.length === 1
          ? "Timesheet entry approved successfully"
          : `${selectedItems.length} timesheet entries approved successfully`
      );
      setSelectedItems([]);
      setShowConfirmDialog(false);
      invalidateAllQueries();
      eventBus.emit("refresh:list", `timesheet${storageKey}`);
    },
    onError: (error) => {
      toast.error("Failed to approve timesheet entries");
      console.error("Error approving timesheet:", error);
    },
  });

  const handleApprove = useCallback(() => {
    approveMutation.mutate(selectedItems);
  }, [selectedItems, approveMutation]);

  const handleSelectionChange = useCallback((items) => {
    setSelectedItems(items);
  }, []);

  return (
    <div className="relative">
      <List
        title="Timesheet Entries to Review"
        storageKey={storageKey}
        endPoint="timesheet"
        filters={filters}
        columns={columns}
        meta={{ fields }}
        selection={true}
        value={selectedItems}
        onChange={handleSelectionChange}
        // href={(item) => ROUTE.TIMESHEET_ENTRY(item.id)}
        // href={null}
      />

      {/* Floating selection action bar — appears when rows are selected */}
      {selectedItems.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-xl border border-border bg-white px-5 py-3 shadow-xl">
          <span className="text-sm font-medium text-muted-foreground">
            {selectedItems.length} {selectedItems.length === 1 ? "entry" : "entries"} selected
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedItems([])}
          >
            Deselect all
          </Button>
          <Button
            size="sm"
            onClick={() => setShowConfirmDialog(true)}
          >
            Approve Selected
          </Button>
        </div>
      )}

      {/* Confirmation dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve timesheet entries?</DialogTitle>
            <DialogDescription>
              This will approve{" "}
              <strong>
                {selectedItems.length} {selectedItems.length === 1 ? "entry" : "entries"}
              </strong>{" "}
              by setting logged hours as approved hours.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <ButtonWithLoading
              variant="default"
              isLoading={approveMutation.isPending}
              onClick={handleApprove}
            >
              Yes, Approve All
            </ButtonWithLoading>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
